/** @jsx jsx */
import { jsx } from '@emotion/core'
import { useState, Fragment, useEffect } from 'react';
import style from './bulkOrderModalStyle';
import { Button, ErrorMessage, SiteLink } from '../../../components';
import env from '../../../env';
import { usePost } from '../../../components';
import utils from '../../../utils';
import { useStore } from '../../../store';

export function BulkOrderModal({ product, selectedPersonalisationOption, onClose }) {
  const post = usePost();
  const { dispatch } = useStore();
  
  // Debug: Log product data
  console.log('=== BULK ORDER MODAL - PRODUCT DATA ===');
  console.log('product.ext:', product.ext);
  console.log('personalisationOption:', product.ext?.personalisationOption);
  console.log('personalisationPrice:', product.ext?.personalisationPrice);
  console.log('=======================================');
  
  // Get available sizes from variantAttributes
  const sizeAttribute = product.variantAttributes?.find(attr => 
    attr.name.toLowerCase() === 'size' || attr.name.toLowerCase() === 'sizes'
  );
  const sizes = sizeAttribute ? sizeAttribute.options.map(opt => opt.value) : [];
  
  // Get personalization settings from product
  // Use selectedPersonalisationOption if provided, otherwise fall back to product config
  const productPersonalisationOption = product.ext && product.ext.personalisationOption ? product.ext.personalisationOption : 'none';
  const personalisationOption = selectedPersonalisationOption || productPersonalisationOption;
  const hasPersonalisation = personalisationOption !== 'none';
  
  // State for bulk quantities
  const [bulkQuantities, setBulkQuantities] = useState(
    sizes.reduce((acc, size) => ({ ...acc, [size]: 0 }), {})
  );
  
  // State for personalization entries
  const [personalisedItems, setPersonalisedItems] = useState([]);
  const [currentPersonalisation, setCurrentPersonalisation] = useState(() => ({
    name: '',
    number: '',
    size: (sizes && sizes.length > 0) ? sizes[0] : '',
    quantity: 1
  }));
  
  // Track if editing an existing item
  const [editingItemId, setEditingItemId] = useState(null);
  
  // Track if adding to cart
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  
  // Track if items were successfully added to cart (to show Go To Cart button)
  const [checkGoToCartBtn, setCheckGoToCartBtn] = useState(false);
  
  // Calculate totals
  const bulkTotal = Object.values(bulkQuantities).reduce((sum, qty) => sum + parseInt(qty || 0), 0);
  const personalisedTotal = personalisedItems.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);
  const grandTotal = bulkTotal + personalisedTotal;
  
  useEffect(() => {
    if (post.done() && isAddingToCart) {
      setIsAddingToCart(false);
      
      if (post.response && post.response.results && post.response.results.cartInfo) {
        // Update cart count
        env.setDevCartId(post.response.results.cartInfo.cartId);
        
        // Check for errors
        const hasErrors = post.response.errors && post.response.errors.length > 0;
        if (!hasErrors) {
          dispatch({
            type: 'SET_STATE', 
            payload: { 
              cartInfo: post.response.results.cartInfo.totalQuantity || 0
            }
          });
          // Show Go To Cart button instead of redirecting
          setTimeout(() => {
            setCheckGoToCartBtn(true);
          }, 500);
        } else {
          // Errors will be displayed by ErrorMessage component
          console.error('Bulk add to cart errors:', post.response.errors);
        }
      } else {
        console.error('Invalid response from bulk add to cart API');
      }
    }
    
    if (post.hasErrors()) {
      console.error('BulkOrderModal: Request failed with errors:', post.errors);
      setIsAddingToCart(false);
      // Show Go To Cart button even if there are errors (for non-compatible products)
      if (post.errors && post.errors[0] && post.errors[0].includes("There are non-compatible products")) {
        setTimeout(() => {
          setCheckGoToCartBtn(true);
        }, 500);
      }
    }
  }, [post, isAddingToCart, dispatch]);
  
  const handleQuantityChange = (size, value) => {
    if (value === undefined || value === null) return;
    const numValue = parseInt(value) || 0;
    setBulkQuantities(prev => ({ ...prev, [size]: Math.max(0, numValue) }));
  };
  
  const handleAddPersonalisation = () => {
    // Validation
    const normalizedOption = personalisationOption === 'name_only' ? 'name' : 
                            personalisationOption === 'number_only' ? 'number' : 
                            personalisationOption;
    
    let isValid = true;
    if (normalizedOption === 'name' && !currentPersonalisation.name.trim()) {
      alert('Please enter a name');
      isValid = false;
    } else if (normalizedOption === 'number' && !currentPersonalisation.number.trim()) {
      alert('Please enter a number');
      isValid = false;
    } else if (normalizedOption === 'name_and_number') {
      if (!currentPersonalisation.name.trim() || !currentPersonalisation.number.trim()) {
        alert('Please enter both name and number');
        isValid = false;
      }
    }
    
    // Validate quantity
    const qty = parseInt(currentPersonalisation.quantity);
    if (isNaN(qty) || qty < 1) {
      alert('Please enter a valid quantity (minimum 1)');
      isValid = false;
    }
    
    if (!isValid) return;
    
    if (editingItemId) {
      // Update existing item
      setPersonalisedItems(prev => prev.map(item => 
        item.id === editingItemId 
          ? { ...currentPersonalisation, quantity: parseInt(currentPersonalisation.quantity) || 1, id: editingItemId }
          : item
      ));
      setEditingItemId(null);
    } else {
      // Add new item
      setPersonalisedItems(prev => [...prev, { 
        ...currentPersonalisation, 
        quantity: parseInt(currentPersonalisation.quantity) || 1,
        id: Date.now() 
      }]);
    }
    
    // Reset form
    setCurrentPersonalisation({
      name: '',
      number: '',
      size: sizes[0] || '',
      quantity: 1
    });
  };
  
  const handleEditPersonalisation = (item) => {
    setCurrentPersonalisation({
      name: item.name,
      number: item.number,
      size: item.size,
      quantity: item.quantity
    });
    setEditingItemId(item.id);
  };
  
  const handleCancelEdit = () => {
    setCurrentPersonalisation({
      name: '',
      number: '',
      size: sizes[0] || '',
      quantity: 1
    });
    setEditingItemId(null);
  };
  
  const handleRemovePersonalisation = (id) => {
    setPersonalisedItems(prev => prev.filter(item => item.id !== id));
    // If we're editing this item, cancel the edit
    if (editingItemId === id) {
      handleCancelEdit();
    }
  };
  
  // Helper function to get all variant options for a given size
  // This ensures we include ALL variant attributes (not just Size) as required by GetMatchingVariant
  const getVariantOptionsForSize = (size) => {
    const variantOptions = [];
    
    // First, add the size attribute
    if (sizeAttribute && sizeAttribute.name) {
      variantOptions.push({ name: sizeAttribute.name, value: size });
    }
    
    // Find a variant that matches this size to get all other variant attributes
    if (product.variants && product.variants.length > 0) {
      const matchingVariant = product.variants.find(variant => {
        if (!variant.variantOptions || !Array.isArray(variant.variantOptions)) return false;
        return variant.variantOptions.some(opt => 
          opt.name && sizeAttribute && opt.name.toLowerCase() === sizeAttribute.name.toLowerCase() && 
          opt.value === size
        );
      });
      
      // If we found a matching variant, include all its variant options
      if (matchingVariant && matchingVariant.variantOptions) {
        matchingVariant.variantOptions.forEach(opt => {
          // Skip if we already added this option (e.g., Size)
          if (!variantOptions.some(existing => 
            existing.name && opt.name && 
            existing.name.toLowerCase() === opt.name.toLowerCase() &&
            existing.value === opt.value
          )) {
            // Skip Personalisation as we'll add that separately if needed
            if (opt.name && opt.name.toLowerCase() !== 'personalisation') {
              variantOptions.push({ name: opt.name, value: opt.value });
            }
          }
        });
      } else {
        // If no matching variant found, include all other variant attributes with their first/default value
        // This handles cases where variants might not be fully populated
        if (product.variantAttributes && Array.isArray(product.variantAttributes)) {
          product.variantAttributes.forEach(attr => {
            // Skip Size as we already added it
            if (attr.name && sizeAttribute && attr.name.toLowerCase() !== sizeAttribute.name.toLowerCase()) {
              // Use the first available option for this attribute
              if (attr.options && attr.options.length > 0 && attr.options[0].value) {
                variantOptions.push({ name: attr.name, value: attr.options[0].value });
              }
            }
          });
        }
      }
    } else {
      // Fallback: if no variants available, include all variant attributes with their first value
      if (product.variantAttributes && Array.isArray(product.variantAttributes)) {
        product.variantAttributes.forEach(attr => {
          // Skip Size as we already added it
          if (attr.name && sizeAttribute && attr.name.toLowerCase() !== sizeAttribute.name.toLowerCase()) {
            // Use the first available option for this attribute
            if (attr.options && attr.options.length > 0 && attr.options[0].value) {
              variantOptions.push({ name: attr.name, value: attr.options[0].value });
            }
          }
        });
      }
    }
    
    return variantOptions;
  };

  const handleAddToCart = async () => {
    if (grandTotal === 0) {
      alert('Please add at least one item');
      return;
    }
    
    // Validate that we have size attribute
    if (!sizeAttribute || !sizeAttribute.name) {
      alert('Error: Product size information is missing. Please refresh the page.');
      return;
    }
    
    setIsAddingToCart(true);
    const cartId = env.getDevCartId();
    const cartIdValue = cartId === 'null' ? null : cartId;
    
    // Build items array
    const items = [];
    
    // Add bulk items (non-personalized)
    sizes.forEach(size => {
      const qty = parseInt(bulkQuantities[size]) || 0;
      if (qty > 0) {
        // Get all variant options for this size (including all variant attributes)
        const variantOptions = getVariantOptionsForSize(size);
        
        items.push({
          productId: product.productId,
          quantity: qty,
          variantOptions: variantOptions
        });
      }
    });
    
    // Add personalized items
    personalisedItems.forEach(item => {
      const normalizedOption = personalisationOption === 'name_only' ? 'name' : 
                              personalisationOption === 'number_only' ? 'number' : 
                              personalisationOption;
      
      let personalisationValue = '';
      if (normalizedOption === 'name') {
        personalisationValue = 'Name: ' + item.name;
      } else if (normalizedOption === 'number') {
        personalisationValue = 'Number: ' + item.number;
      } else if (normalizedOption === 'name_and_number') {
        personalisationValue = 'Name: ' + item.name + ', Number: ' + item.number;
      }
      
      // Get all variant options for this size (including all variant attributes)
      const variantOptions = getVariantOptionsForSize(item.size);
      
      // Add personalisation as a variant option
      variantOptions.push({ name: 'Personalisation', value: personalisationValue });
      
      items.push({
        productId: product.productId,
        quantity: parseInt(item.quantity) || 1,
        variantOptions: variantOptions
      });
    });
    
    // Send bulk add request
    post.send(env.apiBase + "/api/cart/addbulktocart", {
      cartId: cartIdValue,
      items: items
    });
  };
  
  const normalizedPersonalisationOption = personalisationOption === 'name_only' ? 'name' : 
                                          personalisationOption === 'number_only' ? 'number' : 
                                          personalisationOption;
  
  return (
    <div css={style.modalOverlay}>
      <div css={style.modalContainer}>
        <div css={style.modalContent}>
          {/* Close Button */}
          <div css={style.closeButton} onClick={onClose}>
            CLOSE
          </div>
          
          {/* Title */}
          <h2 css={style.modalTitle}>Place Bulk Order</h2>
          <h3 css={style.productName}>{product.productName}</h3>
          {product.description && (
            <p css={style.productDescription} dangerouslySetInnerHTML={{ __html: product.description }}></p>
          )}
          
          <p css={style.instructions}>
            Please add the total QTY you would like to order for each size.
            {hasPersonalisation && " If you require customised products please click the + ADD button options."}
          </p>
          
          {/* Bulk Order Table */}
          <div css={style.tableSection}>
            <div css={style.tableSectionHeader}>
              BULK ORDER - NON PERSONALISATIONS
            </div>
            <div css={style.tableContainer}>
              <table css={style.table}>
                <thead>
                  <tr>
                    <th style={{ backgroundColor: '#061f5c', color: 'white', textAlign: 'center' }}>SIZE</th>
                    {sizes.map(size => (
                      <th key={size} style={{ backgroundColor: '#061f5c', color: 'white', textAlign: 'center' }}>{size}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ backgroundColor: '#061f5c', color: 'white', textAlign: 'center' }}><strong>QTY</strong></td>
                    {sizes.map(size => (
                      <td key={size}>
                        <input
                          type="number"
                          min="0"
                          value={bulkQuantities[size]}
                          onChange={(e) => {
                            const value = e?.currentTarget?.value || e?.target?.value || '0';
                            handleQuantityChange(size, value);
                          }}
                          css={style.qtyInput}
                        />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Personalization Section */}
          {hasPersonalisation && (
            <div css={style.personalisationSection}>
              <div css={style.tableSectionHeader}>
                {normalizedPersonalisationOption === 'name' && 'PERSONALISATION (Name Only)'}
                {normalizedPersonalisationOption === 'number' && 'PERSONALISATION (Number Only)'}
                {normalizedPersonalisationOption === 'name_and_number' && 'PERSONALISATION (Name & Number)'}
              </div>
              
              {product.ext.personalisationPrice && (
                <p style={{ fontSize: '0.95rem', color: '#666', marginBottom: '1rem', marginTop: '0.5rem', fontWeight: '500' }}>
                  Additional charge per personalised item: AU {utils.money.formatMoney(product.ext.personalisationPrice)}
                </p>
              )}
              
              <div css={style.personalisationForm}>
                <div css={style.formRow}>
                  {(normalizedPersonalisationOption === 'name' || normalizedPersonalisationOption === 'name_and_number') && (
                    <div css={style.formField}>
                      <input
                        type="text"
                        placeholder="Name"
                        value={currentPersonalisation.name || ''}
                        onChange={(e) => {
                          const value = e?.currentTarget?.value || e?.target?.value || '';
                          setCurrentPersonalisation(prev => ({ ...prev, name: value }));
                        }}
                        css={style.textInput}
                      />
                    </div>
                  )}
                  
                  {(normalizedPersonalisationOption === 'number' || normalizedPersonalisationOption === 'name_and_number') && (
                    <div css={style.formField}>
                      <input
                        type="text"
                        placeholder="Number"
                        value={currentPersonalisation.number || ''}
                        onChange={(e) => {
                          const value = e?.currentTarget?.value || e?.target?.value || '';
                          // Only allow numbers (0-9)
                          if (value === '' || /^\d+$/.test(value)) {
                            setCurrentPersonalisation(prev => ({ ...prev, number: value }));
                          }
                        }}
                        css={style.textInput}
                        inputMode="numeric"
                        pattern="[0-9]*"
                      />
                    </div>
                  )}
                  
                  <div css={style.formField}>
                    <select
                      value={currentPersonalisation.size || ''}
                      onChange={(e) => {
                        const value = e?.currentTarget?.value || e?.target?.value || '';
                        setCurrentPersonalisation(prev => ({ ...prev, size: value }));
                      }}
                      css={style.selectInput}
                    >
                      {sizes.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div css={style.formField}>
                    <input
                      type="number"
                      min="1"
                      value={currentPersonalisation.quantity || 1}
                      onChange={(e) => {
                        const value = e?.currentTarget?.value || e?.target?.value || '';
                        // Allow empty string temporarily while typing
                        if (value === '') {
                          setCurrentPersonalisation(prev => ({ ...prev, quantity: '' }));
                        } else {
                          const numValue = parseInt(value);
                          setCurrentPersonalisation(prev => ({ 
                            ...prev, 
                            quantity: isNaN(numValue) ? 1 : Math.max(1, numValue)
                          }));
                        }
                      }}
                      onBlur={(e) => {
                        const value = e?.currentTarget?.value || e?.target?.value || '';
                        // On blur, ensure we have a valid number
                        if (value === '' || isNaN(parseInt(value))) {
                          setCurrentPersonalisation(prev => ({ ...prev, quantity: 1 }));
                        }
                      }}
                      css={style.qtyInputSmall}
                      placeholder="Qty"
                    />
                  </div>
                  
                  <div css={style.formField}>
                    {editingItemId ? (
                      <div css={style.buttonGroup}>
                        <button onClick={handleAddPersonalisation} css={style.updateButton}>
                          UPDATE
                        </button>
                        <button onClick={handleCancelEdit} css={style.cancelButton}>
                          CANCEL
                        </button>
                      </div>
                    ) : (
                      <button onClick={handleAddPersonalisation} css={style.addButton}>
                        + ADD
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Personalized Items List */}
              {personalisedItems.length > 0 && (
                <div css={style.personalisedList}>
                  <h4>Personalised Items:</h4>
                  {personalisedItems.map(item => (
                    <div key={item.id} css={style.personalisedItem}>
                      <span>
                        {normalizedPersonalisationOption === 'name' && <Fragment>Name: <strong>{item.name}</strong></Fragment>}
                        {normalizedPersonalisationOption === 'number' && <Fragment>Number: <strong>{item.number}</strong></Fragment>}
                        {normalizedPersonalisationOption === 'name_and_number' && <Fragment>Name: <strong>{item.name}</strong>, Number: <strong>{item.number}</strong></Fragment>}
                        {` - Size: `}<strong>{item.size}</strong>{` - Qty: `}<strong>{item.quantity}</strong>
                      </span>
                      <div>
                        <button onClick={() => handleEditPersonalisation(item)} css={style.editButton}>
                          Edit
                        </button>
                        <button onClick={() => handleRemovePersonalisation(item.id)} css={style.removeButton}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Summary Table - Always show */}
              <div css={style.tableContainer} style={{ marginTop: '1rem' }}>
                <table css={style.table}>
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: '#061f5c', color: 'white', textAlign: 'center' }}>SIZE</th>
                      {sizes.map(size => (
                        <th key={size} style={{ backgroundColor: '#061f5c', color: 'white', textAlign: 'center' }}>{size}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ backgroundColor: '#061f5c', color: 'white', textAlign: 'center' }}><strong>BULK</strong></td>
                      {sizes.map(size => (
                        <td key={size}>{bulkQuantities[size] || 0}</td>
                      ))}
                    </tr>
                    <tr>
                      <td style={{ backgroundColor: '#061f5c', color: 'white', textAlign: 'center' }}><strong>PERSONALISED</strong></td>
                      {sizes.map(size => {
                        const count = personalisedItems
                          .filter(item => item.size === size)
                          .reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);
                        return <td key={size}>{count || 0}</td>;
                      })}
                    </tr>
                    <tr css={style.totalRow}>
                      <td style={{ backgroundColor: '#061f5c', color: 'white', textAlign: 'center' }}><strong>TOTALS</strong></td>
                      {sizes.map(size => {
                        const bulk = parseInt(bulkQuantities[size]) || 0;
                        const personalised = personalisedItems
                          .filter(item => item.size === size)
                          .reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);
                        return <td key={size}><strong>{bulk + personalised}</strong></td>;
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Total and Add to Cart */}
          <div css={style.footer}>
            <div css={style.totalAmount}>
              <strong>Total Items: {grandTotal}</strong>
            </div>
            <div css={style.actions}>
              <ErrorMessage errors={post.errors} />
              <Button onClick={handleAddToCart} status={post.status}>
                Add To Cart
              </Button>
              {checkGoToCartBtn && (
                <SiteLink 
                  className="btn-primary btn" 
                  to="/member/cart" 
                  css={style.goToCartButton}
                  onClick={() => {
                    document.body.style.overflow = "unset";
                  }}
                >
                  GO TO CART
                </SiteLink>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

